import { FindingType_Enumerated, FindingType_Numeric, FindingType_Range, FindingType_Textual } from '@app/advisor-shared';
import { ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable, ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
import { isImplicitPositiveValue, resolveValueDisplay, valueID_No } from '@app/hcs-shared';
import { BadImplementationException, exists, ImplementationMissingException, MissingDataException, StaticLogger } from '@nu-art/ts-common';
export class RelatedFinding {
    finding;
    variable;
    findingResults;
    value;
    attributes;
    diagnosisRelations;
    static createNew = (_finding) => {
        const finding = typeof _finding === 'string' ? ModuleFE_RuntimeFinding.cache.unique(_finding) : _finding;
        if (!finding)
            throw new MissingDataException(`Could not get runtime finding for id ${_finding}`);
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        if (!variable) {
            StaticLogger.logError(`Could not get variable for id ${finding.variableId} for finding ${finding}`);
            return;
        }
        return new RelatedFinding(finding);
    };
    constructor(finding) {
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        if (!variable)
            throw new MissingDataException(`Could not get variable for id ${finding.variableId} for finding ${finding}`);
        this.finding = finding;
        this.variable = variable;
        this.value = this.resolveValue(this.finding);
        this.findingResults = ModuleFE_RuntimeFinding.getFindingResultsForVariable(this.variable);
        this.diagnosisRelations = [];
        if (finding.type === FindingType_Enumerated) {
            this.attributes = finding.attributes?.map(attribute => new RelatedFinding(attribute));
        }
    }
    //######################### Inner Logic #########################
    resolveValue(finding) {
        if (!('_id' in finding)) { //Finding is RuntimeFinding_Attribute
            const value = ModuleFE_Advisor_Value.cache.unique(finding.valueIds[0]);
            if (!value)
                throw new MissingDataException(`Could not get value for id ${finding.valueIds[0]} for finding ${finding}`);
            return value;
        }
        //Finding is runtime, continue based on its type
        switch (finding.type) {
            case FindingType_Range:
                throw new BadImplementationException('Trying to instantiate a range finding as RelatedFinding');
            case FindingType_Numeric:
            case FindingType_Textual:
                return finding.value;
            case FindingType_Enumerated: {
                const value = ModuleFE_Advisor_Value.cache.unique(finding.valueIds[0]);
                if (!value)
                    throw new MissingDataException(`Could not get value for id ${finding.valueIds[0]} for finding ${finding}`);
                return value;
            }
        }
    }
    //######################### Public Logic - Data Registration #########################
    registerDiagnosis = (diagnosisData) => {
        //No double registering diagnosis
        if (this.diagnosisRelations.find(item => item.diagnosis.diagnosis._id === diagnosisData.diagnosis._id))
            return;
        const diagnosisRelation = { diagnosis: diagnosisData };
        const findingId = this.finding._id;
        if (findingId) { //Look for indication in DPVs
            const dpvs = diagnosisData.protocol.careMapOutcomes?.[diagnosisData.diagnosis._id]?.dpvsFindingsMap ?? [];
            const relatedDPVs = dpvs.filter(dpv => dpv.relatedFindings.find(finding => finding.findingId === findingId));
            const indicativeDPVs = relatedDPVs.filter(dpv => dpv.dpv.indicative === 1);
            if (indicativeDPVs.length)
                diagnosisRelation.indication = 1;
        }
        this.diagnosisRelations.push(diagnosisRelation);
    };
    merge = (relatedFinding) => {
        //Merge attributes
        if (relatedFinding.attributes?.length) {
            const attributes = this.attributes ?? [];
            relatedFinding.attributes.forEach(attribute => {
                const existing = attributes.find(item => item.variable._id === attribute.variable._id);
                if (!existing)
                    attributes.push(attribute);
                else
                    existing.merge(attribute);
            });
            this.attributes = attributes;
        }
        //Merge diagnosis relations
        relatedFinding.diagnosisRelations.forEach(relation => {
            if (!this.diagnosisRelations.find(item => item.diagnosis.diagnosis._id === relation.diagnosis.diagnosis._id))
                this.diagnosisRelations.push(relation);
        });
    };
    //######################### Public Logic - Data Acquisition #########################
    getLabel = () => {
        const label = this.variable.name;
        if (!label.includes('.'))
            return label;
        return label.split('.').join(' ');
    };
    getValue = () => {
        switch (typeof this.value) {
            case 'string':
            case 'number':
                return this.value;
            case 'object': {
                const value = this.value;
                // Implicit positives (Yes / CareMap "Suspected") carry no extra info
                // beyond the variable name -> suppress the value text.
                if (isImplicitPositiveValue(value._id))
                    return;
                switch (value._id) {
                    case valueID_No:
                        return 'No';
                    default:
                        return resolveValueDisplay(value);
                }
            }
            default:
                throw new ImplementationMissingException(`For value ${this.value}`);
        }
    };
    getAttributes = () => this.attributes;
    getIndicativeDiagnosis = () => {
        return this.diagnosisRelations.filter(relation => exists(relation.indication));
    };
    getRelatedDiagnosis = () => this.diagnosisRelations;
}
//# sourceMappingURL=RelatedFinding.js.map